/**
 * @fileoverview Own the administration editor for the typed attribute vocabulary used by matters. Backend catalog state determines which attributes and ordering are available; local cell edits remain drafts until saved. `kc-internal-frontend/src/components/CaseDetails/CaseDetails.js` hosts consuming matter views. Attribute-definition changes and editing an individual matter's values are separate operations.
 */

import React, { useCallback, useContext, useEffect, useState } from 'react'
import api from '../../api'
import { AppDataTable, AppColumn } from '../shared/AppDataTable'
import { AppButton, AppTextInput, AppDropdown } from '../shared'
import ToastContext from '../../ToastContext'
import { AdminPage, AdminSection } from './AdminChrome'


const typeOptions = [
    { label: 'Int', value: 'Int' },
    { label: 'Float', value: 'Float' },
    { label: 'Text', value: 'Text' },
    { label: 'Date', value: 'Date' }
]



function CaseAttributeTypes({ embedded = false }) {
    const [caseAttributeTypes, setCaseAttributeTypes] = useState([])
    const showToast = useContext(ToastContext)

    const fetchCaseAttributeTypes = useCallback(

    async (refresh) => {
        api.get(`/case_attribute_types`)
            .then(

            response => {
                const data = response.data
                setCaseAttributeTypes(data)
                console.log("Case AttributeTypes:", data)
                if (refresh) {
                    showToast({ severity: 'info', summary: 'Successful', detail: 'Case Attribute Types Refreshed' })
                }
            })
            .catch(error => {
                console.error(error)
            })
    }, [showToast])

    useEffect(() => {
        fetchCaseAttributeTypes()
    }, [fetchCaseAttributeTypes])

    const onCellEditComplete =

    (e) => {
        return e?.rowData
    }

    const addRow =

    () => {
        const newRow = {
            id: Math.random().toString(),
            Type: '',
            Attribute: '',
        }
        setCaseAttributeTypes(prev => [...prev, newRow])
    }

    const saveChanges =

    async () => {
        const caseAttributeTypesNewIds = caseAttributeTypes.map((row, index) => {
            return { ...row, id: index }
        })
        const rowsToSave = Object.values(caseAttributeTypesNewIds)
        await api.post('/case_attribute_types', rowsToSave)
            .then(response => {
                console.log(response)
                showToast({ severity: 'success', summary: 'Successful', detail: 'Case Attribute Types Updated' })
            })
            .catch(error => {
                console.error(error)
                showToast({ severity: 'error', summary: 'Error', detail: 'Case Attribute Types Not Updated' })
            })
    }

    const deleteRow =

    (rowData) => {
        const shouldDelete = window.confirm('Are you sure you want to delete this row?')
        if (shouldDelete) {
            setCaseAttributeTypes(prev => prev.filter(row => row !== rowData))
        }
    }

    const refreshTable =

    async () => {
        await fetchCaseAttributeTypes(true)
    }

    const Wrapper = embedded ? AdminSection : AdminPage

    return (
        <Wrapper
            title="Case Attribute Types"
            description="Manage the master list of allowed case attributes and their data types. Rows already in use remain visible but locked to protect existing matter data."
            actions={(
                <>
                    <AppButton icon="pi pi-plus" variant='secondary' onClick={addRow} label="Add Attribute" />
                    <AppButton icon="pi pi-refresh" variant='info' onClick={refreshTable} label="Refresh" />
                    <AppButton icon="pi pi-check" variant="success" onClick={saveChanges} label="Save Changes" />
                </>
            )}
        >
            <div
                style={{
                    border: '1px solid var(--kc-surface-edge)',
                    borderRadius: 'var(--admin-radius-lg)',
                    backgroundColor: 'var(--kc-surface-region)',
                    overflow: 'hidden',
                }}
            >
                <AppDataTable
                    value={caseAttributeTypes}
                    editMode="cell"
                    showGridlines
                    reorderableRows
                    responsiveLayout="scroll"
                    tableStyle={{ minWidth: '44rem' }}
                    onRowReorder={(e) => setCaseAttributeTypes(e.value)}
                >
                    <AppColumn rowReorder style={{ width: '3rem' }} />
                    <AppColumn field="Attribute" header="Attribute"
                        editor={(props) => <CellEditor {...props} caseAttributeTypes={caseAttributeTypes} setCaseAttributeTypes={setCaseAttributeTypes} onCellEditComplete={onCellEditComplete} />}
                        editable={(rowData) => !rowData.in_use}
                        onCellEditComplete={onCellEditComplete}
                        style={{ width: '40%' }} />
                    <AppColumn field="Type" header="Type"
                        editor={(props) => <DropdownEditor {...props} caseAttributeTypes={caseAttributeTypes} setCaseAttributeTypes={setCaseAttributeTypes} onCellEditComplete={onCellEditComplete} />}
                        editable={(rowData) => !rowData.in_use}
                        onCellEditComplete={onCellEditComplete}
                        style={{ width: '24%' }} />
                    <AppColumn header="Actions"
                        body={(rowData) => <AppButton icon='pi pi-trash' variant='danger' disabled={rowData.in_use} onClick={() => deleteRow(rowData)} label="Delete" />}
                        style={{ width: '12rem' }} />
                </AppDataTable>
            </div>
        </Wrapper>
    )
}

export default CaseAttributeTypes



const onEditorValueChange = (props, value, caseAttributeTypes, setCaseAttributeTypes, onCellEditComplete) => {
    const updatedAttributes = [...caseAttributeTypes]
    updatedAttributes[props.rowIndex][props.field] = value
    onCellEditComplete({ rowData: updatedAttributes[props.rowIndex] })
    setCaseAttributeTypes(updatedAttributes)
}



const CellEditor = React.memo((props) => {
    const [localValue, setLocalValue] = useState(props.rowData[props.field])
    const { caseAttributeTypes, setCaseAttributeTypes, onCellEditComplete } = props

    const handleBlur =

    () => {
        onEditorValueChange(props, localValue, caseAttributeTypes, setCaseAttributeTypes, onCellEditComplete)
    }

    return (
        <AppTextInput
            type="text"
            value={localValue}
            onChange={(e) => setLocalValue(e.target?.value)}
            onBlur={handleBlur}
            disabled={props.rowData.in_use}
        />
    )
})



const DropdownEditor = React.memo((props) => {
    const [localValue, setLocalValue] = useState(props.rowData[props.field])
    const { caseAttributeTypes, setCaseAttributeTypes, onCellEditComplete } = props

    const handleDropdownChange =

    (e) => {
        setLocalValue(e.value)
        onEditorValueChange(props, e.value, caseAttributeTypes, setCaseAttributeTypes, onCellEditComplete)
    }

    return (
        <AppDropdown
            value={localValue}
            options={typeOptions}
            onChange={handleDropdownChange}
            disabled={props.rowData.in_use}
        />
    )
})
